import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button} from 'views/components';
import {translate} from 'views/module';

import KanbanColumn from './column/KanbanColumn';

import backend from 'utils/backend';

const caseFilters = [{key: 'status', value: ['OPEN', 'PENDING', 'COMPLETE']}];
const caseSort = {sortKey: 'updatedAt', sortDirection: 'DESC'};

const CaseList = (props) => {
  const {
    statusProgressWrap: progressWrap,
  } = props;
  const [cases, setCases] = React.useState([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = React.useState();
  const [stage, setStage] = React.useState('FILL');
  const requestIdRef = React.useRef(0);

  const isLoading = stage === 'LOADING';

  const loadCases = React.useCallback(async (
      startKey,
      shouldReset = false,
      requestId = requestIdRef.current,
      showProgress = true,
  ) => {
    await progressWrap(async () => {
      const caseResponse = await backend.case.queryCase(caseFilters, caseSort, startKey);

      if (requestId !== requestIdRef.current) return;

      setCases((currentCases) => {
        const nextCases = caseResponse?.list || [];
        return shouldReset ? nextCases : [
          ...currentCases,
          ...nextCases,
        ];
      });
      setLastEvaluatedKey(caseResponse?.lastEvaluatedKey);
    }, {setStage, showProgress});
  }, [progressWrap]);

  React.useEffect(() => {
    requestIdRef.current += 1;
    setCases([]);
    setLastEvaluatedKey();
    loadCases(null, true, requestIdRef.current);

    return () => {
      requestIdRef.current += 1;
    };
  }, [loadCases]);

  const loadMoreCases = () => {
    if (!lastEvaluatedKey || isLoading) return;
    loadCases(lastEvaluatedKey, false, requestIdRef.current, false);
  };

  const caseStatuses = [
    {status: 'OPEN', title: translate('case.Status.OPEN.ColumnTitle')},
    {status: 'PENDING', title: translate('case.Status.PENDING.ColumnTitle')},
    {status: 'COMPLETE', title: translate('case.Status.COMPLETE.ColumnTitle')},
  ];
  const getCaseUpdatedAt = (caseData) => {
    const updatedAtTime = new Date(caseData.updatedAt).getTime();

    return Number.isNaN(updatedAtTime) ? 0 : updatedAtTime;
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: {
          md: 'calc(99vh - 32px)',
        },
      }}
    >
      {
        lastEvaluatedKey && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
              px: 3,
              pt: 3,
            }}
          >
            <Button
              disabled={isLoading}
              onClick={loadMoreCases}
              sx={{
                'minWidth': '96px',
                'height': '32px',
                'px': '10px',
                'py': '8px',
                'border': '1px solid #D8D3CC',
                'borderRadius': '7px',
                'backgroundColor': '#FFFFFF',
                'color': '#6B6760',
                'textTransform': 'none',
                'boxShadow': 'none',
                '&:hover': {
                  'border': '1px solid #D8D3CC',
                  'backgroundColor': '#F7F4EF',
                  'boxShadow': 'none',
                },
              }}
              variant='outlined'
            >
              {translate('case.List.LoadMore')}
            </Button>
          </Box>
        )
      }
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          width: '100%',
          p: 3,
          overflowX: 'auto',
        }}
      >
        {
          caseStatuses.map((caseStatus) => {
            const statusCases = cases
                .filter((caseData) => caseData.status === caseStatus.status)
                .sort((firstCase, secondCase) => getCaseUpdatedAt(secondCase) - getCaseUpdatedAt(firstCase));

            return (
              <KanbanColumn
                key={caseStatus.status}
                title={caseStatus.title}
                cases={statusCases}
              />
            );
          })
        }
      </Box>
    </Box>
  );
};

CaseList.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaseList));
